import React, { useEffect, useState } from "react";
import { Plus, Trash2, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Section, Btn, Input, Textarea, Select, Field, cardCls, StatusPill, EmptyState } from "@/components/admin/ui";
import Modal from "@/components/admin/Modal";

const SCOPES = ["group", "venture", "department", "staff"];
const PRIORITIES = ["Low", "Medium", "High"];
const STATUSES = ["Todo", "In Progress", "Done"];
const EMPTY = { title: "", description: "", assigned_scope: "group", venture_id: "", department: "", assignee: "", priority: "Medium", deadline: "", status: "Todo", notes: "" };

export default function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [ventures, setVentures] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/admin/tasks").then(({ data }) => setTasks(data)).catch(() => {});
  useEffect(() => { load(); api.get("/admin/ventures").then(({ data }) => setVentures(data)).catch(() => {}); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const openNew = () => { setForm(EMPTY); setEditing("new"); };
  const openEdit = (t) => { setForm({ ...EMPTY, ...t }); setEditing(t.id); };
  const save = async () => {
    if (!form.title) { toast.error("Title required"); return; }
    if (editing === "new") await api.post("/admin/tasks", form);
    else await api.patch(`/admin/tasks/${editing}`, form);
    toast.success("Saved"); setEditing(null); load();
  };
  const cycle = async (t) => {
    const next = { Todo: "In Progress", "In Progress": "Done", Done: "Todo" }[t.status];
    await api.patch(`/admin/tasks/${t.id}`, { status: next }); load();
  };
  const remove = async (t) => { if (!window.confirm("Delete task?")) return; await api.delete(`/admin/tasks/${t.id}`); load(); };
  const vName = (id) => ventures.find((v) => v.id === id)?.name;

  const cols = STATUSES.map((s) => ({ s, items: tasks.filter((t) => t.status === s) }));

  return (
    <div data-testid="admin-tasks">
      <Section title="Tasks & Projects" subtitle="Assign work to the group, a venture, a department or a person." action={<Btn data-testid="task-add" onClick={openNew}><Plus size={15} /> New Task</Btn>}>
        {tasks.length === 0 ? <EmptyState>No tasks yet.</EmptyState> : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {cols.map(({ s, items }) => (
              <div key={s} className={`${cardCls} p-4`}>
                <div className="flex items-center justify-between mb-4"><StatusPill status={s} /><span className="text-xs text-bd-ivory/30">{items.length}</span></div>
                <div className="space-y-3">
                  {items.map((t) => (
                    <div key={t.id} data-testid={`task-${t.id}`} className="bg-bd-black border border-white/10 p-4">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm text-bd-ivory/90 leading-snug">{t.title}</p>
                        <button onClick={() => cycle(t)} title="Advance status" className="text-bd-ivory/30 hover:text-bd-gold flex-shrink-0"><Check size={15} /></button>
                      </div>
                      <div className="flex items-center gap-2 mt-3 flex-wrap">
                        <span className={`text-[0.6rem] uppercase tracking-wider ${t.priority === "High" ? "text-red-400" : t.priority === "Low" ? "text-bd-ivory/30" : "text-bd-gold"}`}>{t.priority}</span>
                        {t.deadline && <span className="text-[0.6rem] text-bd-ivory/40">· {t.deadline}</span>}
                        {t.venture_id && <span className="text-[0.6rem] text-bd-ivory/40">· {vName(t.venture_id)}</span>}
                      </div>
                      <div className="flex gap-2 mt-3">
                        <button onClick={() => openEdit(t)} className="text-[0.65rem] uppercase tracking-wider text-bd-ivory/40 hover:text-bd-gold">Edit</button>
                        <button onClick={() => remove(t)} className="text-[0.65rem] uppercase tracking-wider text-bd-ivory/40 hover:text-red-400">Delete</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      <Modal open={!!editing} onClose={() => setEditing(null)} title={editing === "new" ? "New Task" : "Edit Task"} testId="task-modal">
        <div className="space-y-5">
          <Field label="Title"><Input data-testid="task-title" value={form.title} onChange={set("title")} /></Field>
          <Field label="Description"><Textarea rows={3} value={form.description} onChange={set("description")} /></Field>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Scope"><Select value={form.assigned_scope} onChange={set("assigned_scope")}>{SCOPES.map((s) => <option key={s} value={s}>{s}</option>)}</Select></Field>
            <Field label="Venture"><Select value={form.venture_id || ""} onChange={set("venture_id")}><option value="">—</option>{ventures.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}</Select></Field>
            <Field label="Department"><Input value={form.department} onChange={set("department")} placeholder="e.g. Marketing" /></Field>
            <Field label="Assignee"><Input value={form.assignee} onChange={set("assignee")} /></Field>
            <Field label="Priority"><Select value={form.priority} onChange={set("priority")}>{PRIORITIES.map((p) => <option key={p}>{p}</option>)}</Select></Field>
            <Field label="Deadline"><Input type="date" value={form.deadline} onChange={set("deadline")} /></Field>
            <Field label="Status"><Select value={form.status} onChange={set("status")}>{STATUSES.map((s) => <option key={s}>{s}</option>)}</Select></Field>
          </div>
          <div className="flex justify-end gap-3">
            <Btn variant="outline" onClick={() => setEditing(null)}>Cancel</Btn>
            <Btn data-testid="task-save" onClick={save}>Save</Btn>
          </div>
        </div>
      </Modal>
    </div>
  );
}
